import React from "react";

/**
 * Reusable Terms of Use content component.
 * Used in both the Terms and Conditions page and the Legal tab.
 */
const TermsOfUseContent = () => {
  return (
    <div className="p-4">
      <h2 className="text-2xl font-semibold mb-1">Docnote Inc.</h2>
      <h3 className="text-xl font-semibold mb-1">Terms of Use</h3>
      <p className="text-base mb-6 font-medium">Effective date: March 3, 2026</p>

      <h4 className="text-xl font-bold mb-3">Summary of Terms of Use</h4>
      <ul className="list-disc ml-4 mb-6">
        <li className="mb-3">
          <b>Relationship between Users: Patient and Healthcare Provider.</b> The User acknowledges and agrees that by accepting a service on the
          Product, such acceptance creates a relationship between the Patient and the Healthcare Provider. The Company does not, and will not be
          deemed to direct, control, or oversee either Users, the Healthcare Provider&apos;s work environment, training provided to a Healthcare
          Provider, or the Healthcare Provider&apos;s performance, or the Healthcare Staff (as defined below). In the event that an issue arises
          between the Users, the Users&apos; sole remedy through the Company shall be termination of the applicable service.
        </li>
        <li className="mb-3">
          <b>Healthcare Provider Selection.</b> The Company makes no representation or warranty as to the accuracy of the information provided by the
          Healthcare Provider or Healthcare Staff, or about any of the Healthcare Provider&apos;s or Healthcare Staff&apos;s skills, experience,
          background, suitability, or education (including, any licenses).
        </li>
        <li className="mb-3">
          <b>Workplace Environment and Training for Healthcare Provider.</b> The User acknowledges and agrees that the Company is in no way
          responsible for the workplace environment or supervision of the Healthcare Provider or the Healthcare Staff during the provision of a
          Service made through the Product.
        </li>
        <li className="mb-3">
          <b>No endorsement as to accuracy:</b> We accept no responsibility for the accuracy of any User Data provided by or created using the Product
          except as otherwise set out in these Terms. The provision or storage of User Data through the Product does not constitute our endorsement or
          warranty as to the compliance of such User Data with applicable privacy legislation, nor to the accuracy, timeliness, materiality,
          completeness, or reliability of such User Data. You are responsible for ensuring that the information you have entered into our system is
          accurate, reliable and complete.
        </li>
      </ul>

      <h4 className="text-xl font-bold mb-3">This Agreement is between:</h4>
      <ol className="list-decimal ml-4 mb-6">
        <li className="mb-3">
          Docnote Inc, a corporation incorporated under the provincial laws of Ontario, with its registered address at 481 London Road, Sarnia, ON,
          N7T 4X3 (referred to in these Terms as <b>&quot;we&quot;</b>, <b>&quot;us&quot;</b> or the <b>&quot;Company&quot;</b>); and
        </li>
        <li className="mb-3">
          The Healthcare Provider who has registered for the use of the Product for the provision of healthcare services to a Patient (the{" "}
          <b>&quot;Healthcare Provider&quot;</b>); and
        </li>
        <li className="mb-3">
          The Healthcare staff who a Healthcare Provider has registered an account on the Product for the Healthcare staff to assist with the
          provision of healthcare services to the User (the <b>&quot;Healthcare Staff&quot;</b>); and
        </li>
        <li className="mb-3">
          The <b>&quot;Patient&quot;</b> (together with the Client, Healthcare Staff, and the Healthcare Provider, collectively, each a{" "}
          <b>&quot;User&quot;</b> or <b>&quot;You&quot;</b>) who has: (a) registered for the use of the Product to receive Medical Services (as
          defined below) from a Healthcare Provider, or; (b) is accessing the Product as a guest of a particular Healthcare Provider (
          <b>&quot;Unregistered Patient of Healthcare Provider&quot;</b>), and together with the Company, each a <b>&quot;Party&quot;</b>, and
          collectively the <b>&quot;Parties&quot;</b>).
        </li>
      </ol>

      <h4 className="text-xl font-bold mb-3">1. PARTIES ACCESSING THE SITE AGREE TO BE BOUND BY OUR TERMS</h4>
      <p className="text-base mb-6 font-medium">
        These terms of use (the <b>&quot;Terms&quot;</b> or <b>&quot;Terms of Use&quot;</b>) govern your access of Docnote, and any other products
        created by the Company from time to time, whether accessed: (a) on a computer connected to the internet at{" "}
        <a href="https://app.docnote.com" style={{ color: "blue" }}>
          https://app.docnote.com
        </a>{" "}
        (the <b>&quot;Website&quot;</b>); (b) on the Company&apos;s social media properties (individually and collectively, (a), and (b) are the
        &quot;Product&quot;), as owned and operated by the Company. These Terms govern the use of User who have accessed or registered for the use of
        the Product, whether as a Patient, Healthcare Staff, or a Healthcare Provider, and are binding on any use of the Product, and apply to You
        from the time that You first access the Product. For clarification, <b>&quot;You&quot;</b> includes terms such as <b>&quot;your&quot;</b> and{" "}
        <b>&quot;yourself&quot;</b>.
      </p>

      <h4 className="text-xl font-bold mb-3">2. APPROVAL OF THE TERMS</h4>
      <p className="text-base mb-6 font-medium">
        It is important that You read these Terms carefully. If You do not agree to these Terms, please do not use the Product or browse the Website.
        By accessing or using the Product, You represent, warrant and signify that: (a) You are at least 18 years of age; (b) You have read,
        understood and agree to be bound by these Terms as they may be amended from time to time; and (c) You have read and understand our Privacy
        Policy, which can be accessed at{" "}
        <a href="https://app.docnote.com/privacy-policy" style={{ color: "blue" }}>
          https://app.docnote.com/privacy-policy
        </a>{" "}
        (the &quot;Privacy Policy&quot;), the terms of which are incorporated herein by reference, and agree to abide by the Privacy Policy.
      </p>
      <p className="text-base mb-6 font-medium">
        You may not use the Product nor accept these Terms if you are not of a legal age to form a binding contract with us. If You accept these
        Terms, You represent that You have the capacity to be bound by them, or if You are acting on behalf of a company or entity, that You have the
        authority to bind such a company or entity (and in which case &quot;You&quot; will refer to the company or entity).
      </p>

      <h4 className="text-xl font-bold mb-3">3. SCOPE OF SERVICE TO BE PROVIDED</h4>
      <p className="text-base mb-6 font-medium">
        <b>Medical Services; Personal Health Information.</b> The Product enables registered Patients to access registered Healthcare Providers to
        request, obtain, and pay for uninsured medical forms (the &quot;Uninsured Medical Services&quot;), and request and obtain medical forms for
        insured services covered under the Ontario Health Insurance Plan (&quot;Insured Medical Services&quot;, together with Uninsured Medical
        Services, the &quot;Medical Services&quot;). The Product also enables Patients to store certain personal and personal health information
        including but not limited to, Patient data, health records, notes taken during consults and case reviews, e-mail communications and/or any
        interaction over video conferencing software (collectively &quot;Personal Health Information&quot;), share their Personal Health Information
        with the Healthcare Providers and Healthcare Staff, and other documents related to the Medical Services.
      </p>

      <h4 className="text-xl font-bold mb-3">4. COMPLIANCE WITH APPLICABLE LAWS</h4>
      <p className="text-base mb-6 font-medium">
        The Product may be made available to Users across Canada. Each User, including Healthcare Providers, is solely responsible for ensuring that
        their use of the Product and the provision of Medical Services complies with all applicable federal, provincial, and territorial laws,
        regulations, and professional licensing requirements in the jurisdiction in which they practice or receive services.
      </p>

      <h4 className="text-xl font-bold mb-3">5. AMENDMENT</h4>
      <p className="text-base mb-3 font-medium">
        We may add to discontinue or revise these Terms or any aspect, mode, design, or service provided under the Product pursuant to the privacy
        legislation and regulations under the Consumer Protection Act, 2002, S.O. 2002, c. 30 (the &quot;Act&quot;) which include but are not limited
        to the:
      </p>
      <ul className="list-disc ml-4 mb-3">
        <li className="mb-3">scope of the features;</li>
        <li className="mb-3">timing of the features;</li>
        <li className="mb-3">software/hardware required for access to the Product; and</li>
        <li className="mb-3">geographic locations or jurisdictions in which certain features may be available.</li>
      </ul>
      <p className="text-base mb-3 font-medium">
        We may amend the Terms without notice for non-material amendments. In the event of a material change, we will provide the User with thirty
        (30) days&apos; notice of a material change in the Terms and conditions (including changes in pricing) via e-mail to the email address
        supplied to us by the User, setting out:
      </p>
      <ul className="list-disc ml-4 mb-3">
        <li className="mb-3">the new or amended agreement terms;</li>
        <li className="mb-3">how such terms read formerly;</li>
        <li className="mb-3">the date of the coming into force of the amendment;</li>
        <li className="mb-3">the means in which You can respond and the effects of not responding;</li>
        <li className="mb-3">the option to either terminate the agreement or retain the existing agreement unchanged; and</li>
        <li className="mb-3">
          the language of this provision with reference to the applicable consumer protection legislation rules for amending these terms and making
          any additional requirements for amendments as prescribed by law (if any).
        </li>
      </ul>
      <p className="text-base mb-3 font-medium">
        We highly recommend that Users read any amendments carefully. Unless explicit consent is required by the law, we have the right to assume that
        You have accepted the change to the terms and conditions, unless You notify us to the contrary, no later than thirty (30) days after the
        amendment comes into force, that You desire to cancel the contract or deregister or unsubscribe from access to the Product.
      </p>
      <p className="text-base mb-6 font-medium">
        We will post the most current Terms on the Website and your use of the Product will be subject to the most current Terms as posted on the
        Website at such time. It is your responsibility to visit this page to find any updates that may have been made to the Terms. You hereby agree
        that the Company shall not be liable to You, your employee, or any other third party for any amendments to the Terms of Use.
      </p>

      <h4 className="text-xl font-bold mb-3">6. PAYMENT TERMS</h4>
      <p className="text-base mb-3 font-medium">
        <b>Rates for the Uninsured Medical Service.</b> The Rates for the Uninsured Medical Services shall be set by the Healthcare Provider in
        accordance with the Ontario Medical Association Guidelines (&quot;Uninsured Medical Services Rates&quot;). Healthcare Provider may alter the
        Rates at their discretion.
      </p>
      <p className="text-base mb-3 font-medium">
        <b>Taxes on Uninsured Medical Services.</b> In the event that Harmonized Sales Tax (or the like) (&quot;HST&quot;) is applicable to the
        Uninsured Medical Services in accordance with laws applicable to Taxes (&quot;Tax Laws&quot;), and the Healthcare Provider has an HST account
        that has been disclosed on the Product (&quot;Healthcare Provider HST Account&quot;), HST will be collected by the Company (&quot;HST
        Collected for Uninsured Medical Services&quot;) and added to the Uninsured Medical Services Rates.
      </p>
      <p className="text-base mb-3 font-medium">
        <b>Healthcare Provider Compliance with Tax Law.</b> The Healthcare Provider represents and warrants that it has complied with all
        registration, reporting, payment, collection and remittance requirements in respect of goods and service tax (&quot;GST&quot;) and provincial
        sales tax or harmonized tax legislation, and any other Taxes related to the Medical Services provided to the Healthcare Provider&apos;s
        Patients. &quot;Taxes&quot; means all federal, provincial, territorial, county, municipal, local or foreign taxes, duties, imposts, levies,
        assessments, tariffs and other charges imposed, assessed or collected by a governmental authority.
      </p>
      <p className="text-base mb-3 font-medium">
        <b>Healthcare Provider Responsibility for Tax Collection.</b> In the event that the Healthcare Provider does not disclose a Healthcare
        Provider HST Account, the Healthcare Provider is solely responsible for collecting any amounts required to be paid to a Taxation Authority
        (&quot;Taxation Authority&quot; means any domestic or foreign government, agency or authority that is entitled to impose Taxes or to
        administer any applicable tax legislation) for the provision of Medical Services to the Healthcare Provider&apos;s Patients (including,
        without limitation, the HST Collected for Uninsured Medical Services) (&quot;Responsibility for Tax Payments for Medical Services&quot;).
      </p>
      <p className="text-base mb-6 font-medium">
        <b>Healthcare Provider Indemnification for Tax Payments for Medical Services.</b> The Healthcare Provider shall indemnify the Company for all
        costs, claims, damages, fees, expenses, penalties and liabilities (collectively, &quot;Medical Tax Claims&quot;) associated with, or arising
        out of a breach of the Responsibility for Tax Payments for Medical Services.
      </p>

      <h4 className="text-xl font-bold mb-3">7. PAYMENT FOR SERVICES</h4>
      <p className="text-base mb-6 font-medium">
        Payment for Uninsured Medical Services shall be made in advance by the Patient and the Company will remit the payment to the Healthcare
        Provider less the applicable fees charged by the Company for payment processing, less any refunds processed by the Healthcare Provider.
      </p>

      <h4 className="text-xl font-bold mb-3">8. REFUNDS</h4>
      <p className="text-base mb-6 font-medium">
        The Healthcare Provider shall have the sole discretion to provide any refunds to the Patient for Uninsured Medical Services. Refunds will be
        processed and remitted to the Patient through the Company&apos;s payment processor. The Company does not provide refunds for any fees charged
        to the Healthcare Provider.
      </p>

      <h4 className="text-xl font-bold mb-3">9. SUBSCRIPTION TERMS</h4>
      <p className="text-base mb-6 font-medium">
        The Healthcare Provider agrees to pay the subscription fees as outlined on the Website or in a separate agreement between the Healthcare
        Provider and the Company. Subscription fees are billed on a recurring basis and the Healthcare Provider agrees to maintain a valid payment
        method on file with the Company.
      </p>

      <h4 className="text-xl font-bold mb-3">10. USER ACCOUNTS</h4>
      <p className="text-base mb-6 font-medium">
        You are responsible for safeguarding your account credentials and for any activities or actions under your account. You agree to immediately
        notify the Company of any unauthorized use of your account or any other breach of security.
      </p>

      <h4 className="text-xl font-bold mb-3">11. INTELLECTUAL PROPERTY</h4>
      <p className="text-base mb-6 font-medium">
        The Product and its entire contents, features, and functionality (including but not limited to all information, software, text, displays,
        images, video, and audio, and the design, selection, and arrangement thereof), are owned by the Company, its licensors, or other providers of
        such material and are protected by Canadian and international copyright, trademark, patent, trade secret, and other intellectual property or
        proprietary rights laws.
      </p>

      <h4 className="text-xl font-bold mb-3">12. PRIVACY AND DATA PROTECTION</h4>
      <p className="text-base mb-6 font-medium">
        Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the Product, to understand our practices
        regarding your personal information.
      </p>

      <h4 className="text-xl font-bold mb-3">13. PROHIBITED USES</h4>
      <p className="text-base mb-3 font-medium">
        You may use the Product only for lawful purposes and in accordance with these Terms. You agree not to use the Product:
      </p>
      <ul className="list-disc ml-4 mb-6">
        <li className="mb-3">In any way that violates any applicable federal, provincial, local, or international law or regulation.</li>
        <li className="mb-3">To transmit, or procure the sending of, any advertising or promotional material without our prior written consent.</li>
        <li className="mb-3">
          To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity.
        </li>
        <li className="mb-3">To engage in any other conduct that restricts or inhibits anyone&apos;s use or enjoyment of the Product.</li>
      </ul>

      <h4 className="text-xl font-bold mb-3">14. DISCLAIMER OF WARRANTIES</h4>
      <p className="text-base mb-6 font-medium">
        THE PRODUCT IS PROVIDED ON AN &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS, WITHOUT ANY WARRANTIES OF ANY KIND, EITHER EXPRESS OR
        IMPLIED. THE COMPANY DISCLAIMS ALL WARRANTIES, INCLUDING, BUT NOT LIMITED TO, IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, AND NON-INFRINGEMENT.
      </p>

      <h4 className="text-xl font-bold mb-3">15. LIMITATION OF LIABILITY</h4>
      <p className="text-base mb-6 font-medium">
        IN NO EVENT WILL THE COMPANY, ITS AFFILIATES, OR THEIR LICENSORS, SERVICE PROVIDERS, EMPLOYEES, AGENTS, OFFICERS, OR DIRECTORS BE LIABLE FOR
        DAMAGES OF ANY KIND, UNDER ANY LEGAL THEORY, ARISING OUT OF OR IN CONNECTION WITH YOUR USE, OR INABILITY TO USE, THE PRODUCT.
      </p>

      <h4 className="text-xl font-bold mb-3">16. INDEMNIFICATION</h4>
      <p className="text-base mb-6 font-medium">
        You agree to defend, indemnify, and hold harmless the Company, its affiliates, licensors, and service providers, and its and their respective
        officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities,
        damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys&apos; fees) arising out of or relating to your
        violation of these Terms or your use of the Product.
      </p>

      <h4 className="text-xl font-bold mb-3">17. GOVERNING LAW</h4>
      <p className="text-base mb-6 font-medium">
        These Terms and any dispute or claim arising out of, or related to, them, their subject matter or their formation shall be governed by and
        construed in accordance with the laws of the Province of Ontario and the laws of Canada applicable therein.
      </p>

      <h4 className="text-xl font-bold mb-3">18. DISPUTE RESOLUTION</h4>
      <p className="text-base mb-6 font-medium">
        Any dispute arising out of or relating to these Terms or the Product shall first be attempted to be resolved through good faith negotiations
        between the parties. If the dispute cannot be resolved through negotiations, it shall be submitted to binding arbitration in accordance with
        the Arbitration Act, 1991 (Ontario).
      </p>

      <h4 className="text-xl font-bold mb-3">19. TERMINATION</h4>
      <p className="text-base mb-6 font-medium">
        We may terminate or suspend your account and bar access to the Product immediately, without prior notice or liability, under our sole
        discretion, for any reason whatsoever and without limitation, including but not limited to a breach of the Terms.
      </p>

      <h4 className="text-xl font-bold mb-3">20. SEVERABILITY</h4>
      <p className="text-base mb-6 font-medium">
        If any provision of these Terms is held by a court or other tribunal of competent jurisdiction to be invalid, illegal, or unenforceable for
        any reason, such provision shall be eliminated or limited to the minimum extent such that the remaining provisions of the Terms will continue
        in full force and effect.
      </p>

      <h4 className="text-xl font-bold mb-3">21. WAIVER</h4>
      <p className="text-base mb-6 font-medium">
        No waiver by the Company of any term or condition set out in these Terms shall be deemed a further or continuing waiver of such term or
        condition or a waiver of any other term or condition.
      </p>

      <h4 className="text-xl font-bold mb-3">22. ASSIGNMENT</h4>
      <p className="text-base mb-6 font-medium">
        The Company may assign or transfer these Terms, in whole or in part, without restriction. You may not assign or transfer any rights or
        obligations under these Terms without the prior written consent of the Company.
      </p>

      <h4 className="text-xl font-bold mb-3">23. FORCE MAJEURE</h4>
      <p className="text-base mb-6 font-medium">
        The Company shall not be liable for any failure or delay in performing its obligations under these Terms where such failure or delay results
        from any cause beyond the reasonable control of the Company.
      </p>

      <h4 className="text-xl font-bold mb-3">24. NOTICES</h4>
      <p className="text-base mb-6 font-medium">
        Any notices or other communications provided by the Company under these Terms will be given by posting to the Product or by email to the email
        address You provided during registration.
      </p>

      <h4 className="text-xl font-bold mb-3">25. ENTIRE AGREEMENT</h4>
      <p className="text-base mb-6 font-medium">
        These Terms will constitute the entire agreement between us and You with respect to the subject matter hereof and all prior oral or written
        agreements, representations or statements with respect to such subject matter are superseded hereby. In the event of a conflict between these
        Terms and the Privacy Policy, the terms and conditions found herein shall prevail.
      </p>

      <h4 className="text-xl font-bold mb-3">26. CONTACT</h4>
      <p className="text-base mb-3 font-medium">
        By providing us with your e-mail address, You agree to receive all required notices electronically, to that e-mail address or by mobile
        notifications via the Product. It is your responsibility to update or change that address, as appropriate.
      </p>
      <p className="text-base mb-6 font-medium">
        If You have any questions or comments regarding these Terms please contact our head office by email{" "}
        <a href="mailto:support@docnote.com" style={{ color: "blue" }}>
          support@docnote.com
        </a>
        .
      </p>
    </div>
  );
};

export default TermsOfUseContent;
